import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export type UsageCardTone = "primary" | "warning"

/** The row tied to the meter, e.g. "AI usage" ←→ "58% remaining". */
export interface UsageCardMeterRow {
  label: ReactNode
  value: ReactNode
  tone?: "default" | "warning"
}

/** A label ←→ value spec row in the block beneath the meter. */
export interface UsageCardRow {
  label: ReactNode
  value: ReactNode
  /** Dims the value — for entitlements the plan does not include. */
  muted?: boolean
}

export interface UsageCardProps {
  title: ReactNode
  caption?: ReactNode
  percentUsed: number
  /**
   * Absolute figures for the footer row. Omit them for a percentage-only card —
   * that is what plan AI allowances use, since the underlying unit (provider
   * spend) is not something we expose.
   */
  used?: number
  total?: number
  remaining?: number
  usedLabel?: string
  remainingLabel?: string
  /** Row directly under the bar, naming what the meter measures. */
  meterRow?: UsageCardMeterRow
  /** Spec rows in a divided block below — plan entitlements, not meters. */
  rows?: UsageCardRow[]
  /** Trailing content, e.g. an at-limit callout. */
  footerSlot?: ReactNode
  tone?: UsageCardTone
  className?: string
}

export function UsageCard({
  title,
  caption,
  percentUsed,
  used,
  total,
  remaining,
  usedLabel = "used",
  remainingLabel = "remaining",
  meterRow,
  rows,
  footerSlot,
  tone = "primary",
  className,
}: UsageCardProps) {
  const pct = Math.max(0, Math.min(100, percentUsed))
  const tint = tone === "warning" ? "bg-warning" : "bg-primary"

  return (
    <div
      className={cn(
        "rounded-md border border-border bg-card p-5 flex flex-col gap-3",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="text-section text-foreground">{title}</span>
          {caption && <span className="text-caption text-muted-foreground">{caption}</span>}
        </div>
        <span className="text-section text-foreground tabular-nums">{pct}%</span>
      </div>

      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500",
            tint,
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {meterRow && (
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-caption text-muted-foreground">{meterRow.label}</span>
          <span
            className={cn(
              "text-caption tabular-nums font-medium",
              meterRow.tone === "warning" ? "text-warning" : "text-foreground",
            )}
          >
            {meterRow.value}
          </span>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          {rows.map((row, i) => (
            <div key={i} className="flex items-baseline justify-between gap-3">
              <span className="text-caption text-muted-foreground">{row.label}</span>
              <span
                className={cn(
                  "text-caption tabular-nums",
                  row.muted
                    ? "text-muted-foreground/70"
                    : "text-foreground font-medium",
                )}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {used !== undefined && total !== undefined && (
        <div className="flex items-center justify-between text-caption text-muted-foreground">
          <span>
            <span className="text-foreground font-medium tabular-nums">
              {used.toLocaleString()}
            </span>
            {" / "}
            <span className="tabular-nums">{total.toLocaleString()}</span>
            {" "}
            {usedLabel}
          </span>
          {remaining !== undefined && (
            <span>
              <span className="text-foreground font-medium tabular-nums">
                {remaining.toLocaleString()}
              </span>
              {" "}
              {remainingLabel}
            </span>
          )}
        </div>
      )}

      {footerSlot}
    </div>
  )
}
